/**
 * useTransferChecklist - Manages checkbox state for entity change transfer flow.
 *
 * For UPDATED entities: tracks which changed items the user has reviewed.
 * For CREATED/DELETED: single acknowledge checkbox to confirm transfer.
 * For SAME: hidden entirely.
 */

import { useCallback, useMemo, useState } from 'react';

import type { ChangeType } from '@/services/api/generated/crossMatterChangesService';

const ACKNOWLEDGE_KEY = '__acknowledge__';

export interface TransferChecklist {
  /** Whether individual per-field checkboxes should render (UPDATED only) */
  showCheckboxes: boolean;
  /** Whether a single card-level acknowledge checkbox should render (CREATED/DELETED) */
  showAcknowledge: boolean;
  /** Whether the acknowledge checkbox is checked */
  acknowledgeChecked: boolean;
  /** Toggle the acknowledge checkbox */
  toggleAcknowledge: () => void;
  /** Whether the transfer button should render at all */
  showButton: boolean;
  /** Whether the transfer button is enabled */
  buttonEnabled: boolean;
  /** Check if a specific item is checked */
  isChecked: (key: string) => boolean;
  /** Toggle a specific item */
  toggle: (key: string) => void;
}

export function useTransferChecklist(
  changeType: ChangeType,
  checkableItems: string[],
): TransferChecklist {
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const toggle = useCallback((key: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const isChecked = useCallback((key: string) => checked.has(key), [checked]);

  const toggleAcknowledge = useCallback(() => {
    toggle(ACKNOWLEDGE_KEY);
  }, [toggle]);

  return useMemo(() => {
    const showButton = changeType !== 'SAME';
    const showCheckboxes = changeType === 'UPDATED';
    const showAcknowledge =
      changeType === 'CREATED' || changeType === 'DELETED';
    const acknowledgeChecked = checked.has(ACKNOWLEDGE_KEY);

    const allFieldsChecked =
      checkableItems.length === 0 ||
      checkableItems.every((k) => checked.has(k));
    const buttonEnabled = showAcknowledge
      ? acknowledgeChecked
      : allFieldsChecked;

    return {
      showCheckboxes,
      showAcknowledge,
      acknowledgeChecked,
      toggleAcknowledge,
      showButton,
      buttonEnabled,
      isChecked,
      toggle,
    };
  }, [
    changeType,
    checkableItems,
    checked,
    isChecked,
    toggle,
    toggleAcknowledge,
  ]);
}
